import React, { useState, useEffect } from 'react';
import api from '../api';
import { 
    Settings, Save, Plus, Trash2, 
    CheckSquare, AlertTriangle, FileSignature, 
    X, List
} from 'lucide-react';

// --- THEME CONSTANTS (Temporary until global context, keeps file self-contained) ---
const THEME = {
    card: '#1e293b', border: '#334155', textSec: '#94a3b8', 
    primary: '#3b82f6', success: '#10b981', danger: '#ef4444', warning: '#facc15'
};

const DailyLogBuilder = () => {
    const [sections, setSections] = useState([]);
    const [permitTypes, setPermitTypes] = useState([]);
    const [registerRequired, setRegisterRequired] = useState(false);
    
    useEffect(() => {
        const load = async () => {
            try {
                const [structRes, permRes] = await Promise.all([
                    api.get('config/daily-log-structure/'), 
                    api.get('config/permits/')
                ]);
                setSections(structRes.data.sections || []);
                setRegisterRequired(structRes.data.register_required || false); 
                setPermitTypes(permRes.data || []);
            } catch (err) { console.error(err); }
        };
        load();
    }, []);

    const addSection = () => setSections([...sections, { title: 'New Section', questions: [] }]);
    const updateSectionTitle = (index, val) => { const n = [...sections]; n[index].title = val; setSections(n); };
    const deleteSection = (index) => { const n = [...sections]; n.splice(index, 1); setSections(n); };

    const addQuestion = (sIdx) => { const n = [...sections]; n[sIdx].questions.push({ text: '', linked_permit: '' }); setSections(n); };
    const updateQuestion = (sIdx, qIdx, field, val) => { const n = [...sections]; n[sIdx].questions[qIdx][field] = val; setSections(n); };
    const removeQuestion = (sIdx, qIdx) => { const n = [...sections]; n[sIdx].questions.splice(qIdx, 1); setSections(n); };

    const saveConfiguration = async () => {
        try {
            await api.post('config/daily-log-structure/', { sections, register_required: registerRequired });
            alert("Configuration saved successfully!");
        } catch (err) { alert("Error saving structure"); }
    };

    return (
        <div className="animate-fade-in">
            {/* HEADER CARD */}
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'30px', background: THEME.card, padding: '20px', borderRadius: '12px', border: `1px solid ${THEME.border}`}}>
                <div style={{display:'flex', alignItems:'center', gap:'12px'}}>
                    <Settings size={28} color={THEME.primary} />
                    <div>
                        <h2 style={{color:'white', margin:0}}>Daily Log Builder</h2>
                        <p style={{color: THEME.textSec, margin:'5px 0 0 0', fontSize:'0.9rem'}}>Define the mandatory questions contractors answer daily.</p>
                    </div>
                </div>
                <button onClick={saveConfiguration} style={{background: THEME.success, color:'white', border:'none', padding:'10px 20px', borderRadius:'8px', fontWeight:'bold', cursor:'pointer', display:'flex', alignItems:'center', gap:'8px'}}>
                    <Save size={18}/> Save Config
                </button>
            </div>

            {/* GLOBAL SETTINGS CARD */}
            <div style={{background: '#0f172a', padding:'20px', borderRadius:'12px', marginBottom:'30px', border: `1px solid ${THEME.warning}`, display:'flex', alignItems:'center', gap:'15px'}}>
                <div style={{background: 'rgba(250, 204, 21, 0.1)', padding:'10px', borderRadius:'8px'}}>
                    <AlertTriangle size={24} color={THEME.warning} />
                </div>
                <div style={{flex:1}}>
                    <label style={{display:'flex', alignItems:'center', cursor:'pointer', color:'white', fontWeight:'bold', fontSize:'1rem'}}>
                        <input 
                            type="checkbox" 
                            checked={registerRequired} 
                            onChange={(e) => setRegisterRequired(e.target.checked)}
                            style={{width:'20px', height:'20px', marginRight:'15px', accentColor: THEME.warning}}
                        />
                        Require Daily Work Register (Employee Names)
                    </label>
                    <div style={{color: THEME.textSec, fontSize:'0.85rem', fontStyle:'italic', marginTop:'4px'}}>
                        Contractors must select specific workers from their roster for every shift.
                    </div>
                </div>
            </div>

            {/* SECTIONS BUILDER */}
            {sections.map((section, sIdx) => (
                <div key={sIdx} style={{background: THEME.card, padding:'25px', borderRadius:'12px', border: `1px solid ${THEME.border}`, marginBottom:'25px'}}>
                    
                    {/* Section Header */}
                    <div style={{display:'flex', gap:'15px', marginBottom:'20px', alignItems:'center'}}>
                        <div style={{background:'#0f172a', padding:'10px', borderRadius:'8px', border:`1px solid ${THEME.border}`}}>
                            <List size={24} color={THEME.textSec}/>
                        </div>
                        <input 
                            value={section.title} 
                            onChange={(e) => updateSectionTitle(sIdx, e.target.value)}
                            placeholder="Section Title (e.g., High Risk Works)"
                            style={{
                                flex:1, padding:'12px', fontSize:'1.1rem', fontWeight:'bold', 
                                background: '#020617', border: `1px solid ${THEME.border}`, 
                                color:'white', borderRadius:'8px'
                            }}
                        />
                        <button onClick={() => deleteSection(sIdx)} style={{background: 'rgba(239, 68, 68, 0.2)', color: THEME.danger, border: `1px solid ${THEME.danger}`, padding:'12px', borderRadius:'8px', cursor:'pointer'}}>
                            <Trash2 size={20}/>
                        </button>
                    </div>

                    {/* Questions List */}
                    <div style={{display:'flex', flexDirection:'column', gap:'10px'}}>
                        {section.questions.map((q, qIdx) => (
                            <div key={qIdx} style={{display:'grid', gridTemplateColumns:'2fr 1fr 40px', gap:'15px', alignItems:'center', background:'rgba(255,255,255,0.02)', padding:'10px', borderRadius:'8px'}}>
                                <div style={{position:'relative'}}>
                                    <CheckSquare size={16} style={{position:'absolute', left:'10px', top:'12px', color:THEME.textSec}}/>
                                    <input 
                                        value={q.text} 
                                        onChange={(e) => updateQuestion(sIdx, qIdx, 'text', e.target.value)}
                                        placeholder="Question Text..."
                                        style={{padding:'10px 10px 10px 35px', background:'#0f172a', border: `1px solid ${THEME.border}`, color:'white', borderRadius:'6px', width:'100%', boxSizing:'border-box'}}
                                    />
                                </div>
                                <div style={{position:'relative'}}>
                                    <FileSignature size={16} style={{position:'absolute', left:'10px', top:'12px', color: q.linked_permit ? THEME.warning : THEME.textSec}}/>
                                    <select 
                                        value={q.linked_permit || ''} 
                                        onChange={(e) => updateQuestion(sIdx, qIdx, 'linked_permit', e.target.value)}
                                        style={{
                                            padding:'10px 10px 10px 35px', background:'#0f172a', border: `1px solid ${q.linked_permit ? THEME.warning : THEME.border}`, 
                                            color: q.linked_permit ? THEME.warning : THEME.textSec, borderRadius:'6px', width:'100%', boxSizing:'border-box'
                                        }}
                                    >
                                        <option value="">(No Permit Trigger)</option>
                                        {permitTypes.map(p => <option key={p.id} value={p.id}>Trigger: {p.name}</option>)}
                                    </select>
                                </div>
                                <button onClick={() => removeQuestion(sIdx, qIdx)} style={{background:'transparent', border:'none', cursor:'pointer', color: THEME.danger, display:'flex', alignItems:'center', justifyContent:'center'}}>
                                    <X size={20}/>
                                </button>
                            </div>
                        ))}
                    </div>

                    <button onClick={() => addQuestion(sIdx)} style={{marginTop:'15px', color: THEME.primary, background:'transparent', border: `1px dashed ${THEME.primary}`, padding:'8px 16px', borderRadius:'6px', cursor:'pointer', width:'100%', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px'}}>
                        <Plus size={16}/> Add Question
                    </button>
                </div>
            ))}

            <button onClick={addSection} style={{width:'100%', padding:'15px', background: THEME.primary, color:'white', border:'none', borderRadius:'12px', fontSize:'1.1rem', fontWeight:'bold', cursor:'pointer', boxShadow:'0 4px 6px -1px rgba(0, 0, 0, 0.1)', display:'flex', alignItems:'center', justifyContent:'center', gap:'10px'}}>
                <Plus size={20}/> Add New Section
            </button>
        </div>
    );
};

export default DailyLogBuilder;